import { useEffect, useState } from 'react';
import type { Patient } from '@/models/patient';
import { useActivePatients } from '@/stores/patients';
import {
  cancelTransfer,
  initiateTransfer,
} from '@/services/api/transfer';
import { patientToSnapshot } from './snapshot';
import { useOutgoingTransferStatus } from './useOutgoingTransferStatus';

type Stage = 'select' | 'enterCode' | 'awaiting';

const AWAITING_TIMEOUT_MS = 90_000;
const RESULT_CLOSE_DELAY_MS = 2000;

type Props = {
  open: boolean;
  uid: string;
  onClose: () => void;
};

export function TransferDialog({ open, uid, onClose }: Props) {
  const activePatients = useActivePatients((s) => s.list);
  const refreshActive = useActivePatients((s) => s.refresh);

  const [stage, setStage] = useState<Stage>('select');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [code, setCode] = useState('');
  const [transferId, setTransferId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const status = useOutgoingTransferStatus(uid, transferId);

  useEffect(() => {
    if (!open) return;
    setStage('select');
    setSelectedIds(new Set(activePatients.map((p) => p.id)));
    setCode('');
    setTransferId(null);
    setError(null);
    setBusy(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (stage !== 'awaiting' || !status) return;
    if (status === 'pending') return;
    if (status === 'accepted') {
      void handleAccepted();
      return;
    }
    setError(
      status === 'declined'
        ? 'Votre collègue a refusé le transfert.'
        : 'Le transfert a expiré.',
    );
    setTransferId(null);
    const t = setTimeout(onClose, RESULT_CLOSE_DELAY_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, stage]);

  useEffect(() => {
    if (stage !== 'awaiting' || !transferId) return;
    const timer = setTimeout(() => {
      void cancelTransfer(transferId).catch(() => {});
      setError('Pas de réponse du collègue. Réessayez.');
      setTransferId(null);
      setTimeout(onClose, RESULT_CLOSE_DELAY_MS);
    }, AWAITING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [stage, transferId, onClose]);

  async function handleAccepted() {
    setTransferId(null);
    await refreshActive(uid);
    onClose();
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function onSendCode() {
    if (busy) return;
    if (!/^[a-z0-9]{6}$/.test(code)) {
      setError('Le code doit comporter 6 caractères.');
      return;
    }
    const selected = activePatients.filter((p) => selectedIds.has(p.id));
    if (selected.length === 0) {
      setError('Sélectionnez au moins un patient.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { transferId: id } = await initiateTransfer({
        code,
        patientIds: selected.map((p) => p.id),
        patientSnapshots: selected.map(patientToSnapshot),
      });
      setTransferId(id);
      setStage('awaiting');
    } catch (e) {
      setError(humanizeError(e));
    } finally {
      setBusy(false);
    }
  }

  async function onCancelAwaiting() {
    if (transferId) {
      try {
        await cancelTransfer(transferId);
      } catch {
        // best-effort
      }
    }
    setTransferId(null);
    setError(null);
    setStage('select');
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="bg-brand-dark text-white rounded-2xl shadow-card max-w-md w-full p-6 flex flex-col gap-4"
      >
        {stage === 'select' && (
          <>
            <h2 className="text-lg font-semibold">Transférer des patients</h2>
            <p className="text-sm text-white/80">
              Sélectionnez les patients à transférer à un collègue qui utilise
              ShiftPal.
            </p>
            <ul className="flex flex-col gap-2 max-h-64 overflow-y-auto">
              {activePatients.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center gap-3 bg-white/5 rounded-xl px-3 py-2"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(p.id)}
                    onChange={() => toggleSelect(p.id)}
                    className="w-4 h-4 accent-brand-orange"
                  />
                  <PatientLine p={p} />
                </li>
              ))}
            </ul>
            {error && <p className="text-sm text-red-300">{error}</p>}
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                className="flex-1 rounded-pill bg-white/15 text-white py-2 font-semibold"
                onClick={onClose}
              >
                Annuler
              </button>
              <button
                type="button"
                className="flex-1 rounded-pill bg-brand-orange text-white py-2 font-semibold disabled:opacity-50"
                disabled={selectedIds.size === 0}
                onClick={() => {
                  setError(null);
                  setStage('enterCode');
                }}
              >
                Suivant
              </button>
            </div>
          </>
        )}

        {stage === 'enterCode' && (
          <>
            <h2 className="text-lg font-semibold">Code du collègue</h2>
            <p className="text-sm text-white/80">
              Demandez à votre collègue d'ouvrir « Recevoir un transfert » et
              tapez le code à 6 caractères ci-dessous.
            </p>
            <input
              inputMode="text"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              autoCapitalize="off"
              pattern="[a-z0-9]{6}"
              maxLength={6}
              autoFocus
              value={code}
              onChange={(e) =>
                setCode(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))
              }
              className="w-full rounded-pill bg-white text-brand-darkest px-5 py-3 text-center text-2xl tracking-[0.3em] shadow-card outline-none"
            />
            {error && <p className="text-sm text-red-300">{error}</p>}
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                className="flex-1 rounded-pill bg-white/15 text-white py-2 font-semibold"
                disabled={busy}
                onClick={() => setStage('select')}
              >
                Retour
              </button>
              <button
                type="button"
                className="flex-1 rounded-pill bg-brand-orange text-white py-2 font-semibold disabled:opacity-50"
                disabled={busy || code.length !== 6}
                onClick={() => void onSendCode()}
              >
                {busy ? 'Envoi…' : 'Envoyer'}
              </button>
            </div>
          </>
        )}

        {stage === 'awaiting' && (
          <>
            <h2 className="text-lg font-semibold">
              {error ? 'Transfert non complété' : 'En attente du collègue…'}
            </h2>
            {!error && (
              <p className="text-sm text-white/80">
                Patientez pendant que votre collègue accepte le transfert.
              </p>
            )}
            <div className="flex items-center justify-center py-6">
              {error ? (
                <p className="text-sm text-red-300">{error}</p>
              ) : (
                <div className="w-8 h-8 rounded-full border-2 border-white/40 border-t-brand-orange animate-spin" />
              )}
            </div>
            {!error && (
              <button
                type="button"
                className="rounded-pill bg-white/15 text-white py-2 font-semibold"
                onClick={() => void onCancelAwaiting()}
              >
                Annuler
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function PatientLine({ p }: { p: Patient }) {
  const name = `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || '—';
  return (
    <div className="flex flex-col text-sm leading-tight">
      <span className="font-medium">{name}</span>
      <span className="text-white/60 text-xs">
        {[p.civ, p.dx].filter(Boolean).join(' · ') || '—'}
      </span>
    </div>
  );
}

function humanizeError(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e);
  if (raw.includes('code_invalid')) return 'Code invalide ou expiré.';
  if (raw.includes('self_transfer'))
    return 'Vous ne pouvez pas vous transférer à vous-même.';
  if (raw.includes('already_resolved')) return 'Ce transfert a déjà été traité.';
  if (raw.includes('not_found')) return 'Transfert introuvable.';
  return 'Une erreur est survenue. Réessayez.';
}
